import { useEffect, useCallback, useMemo, useRef } from 'react';
import {
  VStack,
  HStack,
  Text,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Alert,
  AlertIcon,
  Box,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Tooltip,
  Badge,
} from '@chakra-ui/react';
import { AlgorithmSettingsProps } from 'src/components/auto-shapes/types';
import {
  AutoShapeAlgorithmType,
  RoomDetection2DOptions,
} from 'src/gql/graphql';
import { useSheetViewerV2 } from 'src/components/viewers/SheetsViewerV2/hooks/useSheetViewerV2';

// Default values for CALIBRATED pages (physical units)
const DEFAULTS_CALIBRATED = {
  wallThickness: 200, // mm
  maxGap: 800, // mm
  minRoomArea: 1, // m²
  simplifyTolerance: 50, // mm
};

// Default values for NON-CALIBRATED pages (pixel units)
const DEFAULTS_UNCALIBRATED = {
  wallThickness: 2, // px
  maxGap: 90, // px
  minRoomArea: 500, // px²
  simplifyTolerance: 3, // px
};

// Slider configurations for calibrated pages (physical units)
const SLIDER_CONFIGS_CALIBRATED = {
  wallThickness: { min: 50, max: 500, step: 10, unit: 'mm' },
  maxGap: { min: 200, max: 2000, step: 50, unit: 'mm' },
  minRoomArea: { min: 0.5, max: 10, step: 0.5, unit: 'm²', maxInput: 100 },
  simplifyTolerance: { min: 10, max: 200, step: 5, unit: 'mm' },
};

// Slider configurations for non-calibrated pages (pixel units)
const SLIDER_CONFIGS_UNCALIBRATED = {
  wallThickness: { min: 1, max: 10, step: 1, unit: 'px' },
  maxGap: { min: 20, max: 200, step: 5, unit: 'px' },
  minRoomArea: {
    min: 100,
    max: 5000,
    step: 100,
    unit: 'px²',
    maxInput: 100000,
  },
  simplifyTolerance: { min: 1, max: 20, step: 1, unit: 'px' },
};

function SliderWithLabel({
  label,
  value,
  min,
  max,
  step,
  onChange,
  displayValue,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (value: number) => void;
  displayValue?: string;
}) {
  return (
    <VStack align="stretch" spacing={1}>
      <HStack justify="space-between">
        <Text color="gray.500" fontSize="sm">
          {label}
        </Text>
        <Text fontSize="sm" color="gray.600" fontFamily="mono">
          {displayValue ?? value}
        </Text>
      </HStack>
      <Slider
        colorScheme="cyan"
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={onChange}
      >
        <SliderTrack>
          <SliderFilledTrack />
        </SliderTrack>
        <SliderThumb boxSize={4} />
      </Slider>
    </VStack>
  );
}

function SheetInfo({
  hasSheet,
  sheetName,
  pageNumber,
  isCalibrated,
}: {
  hasSheet: boolean;
  sheetName: string;
  pageNumber: number;
  isCalibrated: boolean;
}) {
  if (!hasSheet) {
    return (
      <Alert status="warning" borderRadius="md">
        <AlertIcon />
        <Text fontSize="sm">Open a sheet to detect rooms.</Text>
      </Alert>
    );
  }
  return (
    <Box
      p={3}
      bg="gray.50"
      _dark={{ bg: 'gray.700' }}
      borderRadius="md"
      borderWidth="1px"
    >
      <HStack justify="space-between" mb={1}>
        <Text fontSize="sm" fontWeight="medium">
          Target Sheet
        </Text>
        <Badge colorScheme={isCalibrated ? 'green' : 'orange'} fontSize="xs">
          {isCalibrated ? 'Calibrated' : 'Not Calibrated'}
        </Badge>
      </HStack>
      <Text fontSize="sm" color="gray.600">
        {sheetName} - Page {pageNumber}
      </Text>
      {!isCalibrated && (
        <Text fontSize="xs" color="orange.600" mt={1}>
          For best results, calibrate the sheet first.
        </Text>
      )}
    </Box>
  );
}

function useCalibrationStatus(sheetId: string | undefined, pageNumber: number) {
  const { sheets } = useSheetViewerV2();

  return useMemo(() => {
    const sheet = sheets?.find((s) => s.id === sheetId);
    if (!sheet?.sheetPageCalibrations) return false;
    return sheet.sheetPageCalibrations.some(
      (cal) => cal.pageNumber === pageNumber && cal.calibration
    );
  }, [sheets, sheetId, pageNumber]);
}

type ParameterSliderProps = {
  label: string;
  tooltip: string;
  value: number;
  defaultValue: number;
  config: { min: number; max: number; step: number; unit: string };
  onChange: (v: number) => void;
};

function ParameterSlider({
  label,
  tooltip,
  value,
  defaultValue,
  config,
  onChange,
}: ParameterSliderProps) {
  return (
    <Tooltip label={tooltip} hasArrow>
      <Box>
        <SliderWithLabel
          label={label}
          value={value ?? defaultValue}
          min={config.min}
          max={config.max}
          step={config.step}
          onChange={onChange}
          displayValue={`${value ?? defaultValue}${config.unit}`}
        />
      </Box>
    </Tooltip>
  );
}

type MinRoomAreaInputProps = {
  value: number;
  defaultValue: number;
  config: {
    min: number;
    max: number;
    step: number;
    unit: string;
    maxInput: number;
  };
  onChange: (v: number) => void;
  isCalibrated: boolean;
};

function MinRoomAreaInput({
  value,
  defaultValue,
  config,
  onChange,
  isCalibrated,
}: MinRoomAreaInputProps) {
  const tooltip = isCalibrated
    ? 'Minimum area for a region to be detected as a room (m²)'
    : 'Minimum area for a region to be detected as a room (px²)';

  return (
    <Tooltip label={tooltip} hasArrow>
      <VStack align="stretch" spacing={1}>
        <HStack justify="space-between">
          <Text color="gray.500" fontSize="sm">
            Min Room Area
          </Text>
          <Text fontSize="sm" color="gray.600" fontFamily="mono">
            {value ?? defaultValue}
            {config.unit}
          </Text>
        </HStack>
        <NumberInput
          size="sm"
          value={value ?? defaultValue}
          min={config.min}
          max={config.maxInput}
          step={config.step}
          onChange={(_, val) => onChange(val)}
        >
          <NumberInputField />
          <NumberInputStepper>
            <NumberIncrementStepper />
            <NumberDecrementStepper />
          </NumberInputStepper>
        </NumberInput>
      </VStack>
    </Tooltip>
  );
}

function useSheetContext() {
  const { activeSheet, sheets, pagination } = useSheetViewerV2();

  const currentSheetId = activeSheet?.data?.id;
  const currentPageNumber =
    pagination?.pageNumber ?? activeSheet?.data?.page ?? 1;
  const currentSheet = sheets?.find((s) => s.id === currentSheetId);
  const sheetName = currentSheet?.name ?? 'Unknown Sheet';

  return { currentSheetId, currentPageNumber, sheetName };
}

function useSyncSheetOptions(
  currentSheetId: string | undefined,
  currentPageNumber: number,
  options: RoomDetection2DOptions,
  isCalibrated: boolean,
  onChange: (opts: RoomDetection2DOptions) => void
) {
  // Track whether we've initialized for this sheet/calibration combination
  const initializedRef = useRef<string | null>(null);
  const calibrationKey = `${currentSheetId}-${currentPageNumber}-${isCalibrated}`;

  useEffect(() => {
    if (!currentSheetId) return;

    // Update if sheet changed OR if this is first time seeing this calibration state
    const needsUpdate =
      currentSheetId !== options.sheetId ||
      initializedRef.current !== calibrationKey;

    if (needsUpdate) {
      const newDefaults = isCalibrated
        ? DEFAULTS_CALIBRATED
        : DEFAULTS_UNCALIBRATED;
      onChange({
        sheetId: currentSheetId,
        pageNumber: currentPageNumber,
        wallThickness: newDefaults.wallThickness,
        maxGap: newDefaults.maxGap,
        minRoomArea: newDefaults.minRoomArea,
        simplifyTolerance: newDefaults.simplifyTolerance,
      });
      initializedRef.current = calibrationKey;
    }
  }, [
    currentSheetId,
    currentPageNumber,
    options.sheetId,
    isCalibrated,
    calibrationKey,
    onChange,
  ]);
}

function useValiditySync(
  options: RoomDetection2DOptions,
  onValidityChange: (valid: boolean) => void
) {
  useEffect(() => {
    const isValid =
      Boolean(options.sheetId) && Number.isFinite(options.pageNumber);
    onValidityChange(isValid);
  }, [options.sheetId, options.pageNumber, onValidityChange]);
}

type ParameterControlsProps = {
  options: RoomDetection2DOptions;
  defaults: typeof DEFAULTS_CALIBRATED;
  configs: typeof SLIDER_CONFIGS_CALIBRATED;
  isCalibrated: boolean;
  updateOption: (key: keyof RoomDetection2DOptions, val: number) => void;
};

function ParameterControls({
  options,
  defaults,
  configs,
  isCalibrated,
  updateOption,
}: ParameterControlsProps) {
  const unitLabel = isCalibrated ? '(physical units)' : '';
  const lengthUnit = isCalibrated ? 'millimeters' : 'pixels';

  return (
    <VStack spacing={4} align="stretch">
      <Text fontWeight="medium" fontSize="sm" color="gray.700">
        Detection Parameters
        {unitLabel && (
          <Text as="span" fontSize="xs" color="gray.500" ml={2}>
            {unitLabel}
          </Text>
        )}
      </Text>

      <ParameterSlider
        label="Wall Thickness"
        tooltip={`Wall thickness between rooms in ${lengthUnit}`}
        value={options.wallThickness ?? defaults.wallThickness}
        defaultValue={defaults.wallThickness}
        config={configs.wallThickness}
        onChange={(v) => updateOption('wallThickness', v)}
      />

      <ParameterSlider
        label="Max Gap"
        tooltip={`Maximum gap size to close (${configs.maxGap.unit})`}
        value={options.maxGap ?? defaults.maxGap}
        defaultValue={defaults.maxGap}
        config={configs.maxGap}
        onChange={(v) => updateOption('maxGap', v)}
      />

      <MinRoomAreaInput
        value={options.minRoomArea ?? defaults.minRoomArea}
        defaultValue={defaults.minRoomArea}
        config={configs.minRoomArea}
        onChange={(v) => updateOption('minRoomArea', v)}
        isCalibrated={isCalibrated}
      />

      <ParameterSlider
        label="Simplification"
        tooltip={`Polygon simplification (${configs.simplifyTolerance.unit})`}
        value={options.simplifyTolerance ?? defaults.simplifyTolerance}
        defaultValue={defaults.simplifyTolerance}
        config={configs.simplifyTolerance}
        onChange={(v) => updateOption('simplifyTolerance', v)}
      />
    </VStack>
  );
}

export function RoomDetection2DSettings({
  value,
  onChange,
  onValidityChange,
}: AlgorithmSettingsProps<AutoShapeAlgorithmType.RoomDetection_2D>) {
  const { currentSheetId, currentPageNumber, sheetName } = useSheetContext();
  const isCalibrated = useCalibrationStatus(currentSheetId, currentPageNumber);

  const defaults = isCalibrated ? DEFAULTS_CALIBRATED : DEFAULTS_UNCALIBRATED;
  const configs = isCalibrated
    ? SLIDER_CONFIGS_CALIBRATED
    : SLIDER_CONFIGS_UNCALIBRATED;

  useSyncSheetOptions(
    currentSheetId,
    currentPageNumber,
    value,
    isCalibrated,
    onChange
  );
  useValiditySync(value, onValidityChange);

  const updateOption = useCallback(
    (key: keyof RoomDetection2DOptions, val: number) => {
      onChange({ ...value, [key]: val });
    },
    [value, onChange]
  );

  return (
    <VStack spacing={4} align="stretch" width="100%">
      <SheetInfo
        hasSheet={Boolean(value.sheetId)}
        sheetName={sheetName}
        pageNumber={value.pageNumber}
        isCalibrated={isCalibrated}
      />

      <ParameterControls
        options={value}
        defaults={defaults}
        configs={configs}
        isCalibrated={isCalibrated}
        updateOption={updateOption}
      />
    </VStack>
  );
}
